/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
    AgeDemographics,
    CityDemographics,
    Followers,
    GenderDemographics,
    Interactions,
    Views,
} from "./index";
import { Link } from "react-router-dom";

const DataUnavailableMessage = ({
    icon: IconComponent,
    title,
    message,
    suggestion,
}) => {
    const blueThemeColor = "#399BFF";
    return (
        <Box
            margin={"1.5rem 0"}
            sx={{
                padding: "1.5rem",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: `1px solid #e0e0e0`,
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                {IconComponent && (
                    <IconComponent sx={{ color: blueThemeColor }} />
                )}
                <Typography variant="h6" sx={{ color: blueThemeColor }}>
                    {title}
                </Typography>
            </Box>
            {message && (
                <Box display="flex" alignItems="flex-start" gap={1} mt={2}>
                    <Typography color="text.secondary">{message}</Typography>
                </Box>
            )}
            {suggestion && (
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Typography variant="body2" fontStyle="italic">
                        {suggestion}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

/**
 * Transforms the new API response format to match the old component's expected format
 */
const transformChannelData = (channelData) => {
    if (!channelData?.data?.analytics) return null;

    const { analytics, engagement, profile } = channelData.data;
    const geo_config = channelData.geo_config;

    // Safe destructuring with defaults
    const insights_breakdown = analytics?.insights_breakdown || [];
    const demographics = analytics?.demographics || [];
    const statistics = analytics?.statistics || [];

    // Helper to get breakdown data
    const getBreakdownData = (metricName, breakdownType) => {
        if (
            !Array.isArray(insights_breakdown) ||
            insights_breakdown.length === 0
        ) {
            return null;
        }

        // Aggregate across all dates
        const allBreakdowns = insights_breakdown
            .filter((ib) => ib?.insights)
            .flatMap((ib) =>
                ib.insights.filter(
                    (insight) =>
                        insight?.metric === metricName &&
                        insight?.breakdown?.[breakdownType]
                )
            );

        if (allBreakdowns.length === 0) return null;

        // Aggregate values by dimension
        const aggregated = {};
        let totalValue = 0;

        allBreakdowns.forEach((breakdown) => {
            const breakdownData = breakdown?.breakdown?.[breakdownType];
            if (Array.isArray(breakdownData)) {
                breakdownData.forEach((item) => {
                    if (item?.name != null && item?.value != null) {
                        aggregated[item.name] =
                            (aggregated[item.name] || 0) + item.value;
                        totalValue += item.value;
                    }
                });
            }
        });

        const results = Object.entries(aggregated).map(([name, value]) => ({
            dimension_values: [name],
            value,
        }));

        return results.length > 0
            ? {
                  results,
                  total: totalValue,
              }
            : null;
    };

    // Transform Views Data
    const viewsBreakdown = getBreakdownData("views", "follow_type");
    const viewsData =
        viewsBreakdown && viewsBreakdown.results.length > 0
            ? {
                  followers: {
                      raw_data: {
                          description:
                              "Number of times your content was viewed, broken down by followers and non-followers.",
                          total_value: {
                              value: viewsBreakdown.total,
                              breakdowns: [{ results: viewsBreakdown.results }],
                          },
                      },
                  },
                  content: {
                      raw_data: {
                          description:
                              "Views by content type (Reels, Posts, Stories, etc.)",
                          total_value: {
                              value: viewsBreakdown.total,
                              breakdowns: [
                                  {
                                      results:
                                          getBreakdownData(
                                              "views",
                                              "media_product_type"
                                          )?.results || [],
                                  },
                              ],
                          },
                      },
                  },
              }
            : null;

    // Transform Interactions Data
    const interactionsBreakdown = getBreakdownData(
        "total_interactions",
        "media_product_type"
    );
    const interactionsData =
        interactionsBreakdown && interactionsBreakdown.results.length > 0
            ? {
                  raw_data: {
                      description:
                          "Total interactions (likes, comments, shares, saves) by content type.",
                      total_value: {
                          value: interactionsBreakdown.total,
                          breakdowns: [
                              { results: interactionsBreakdown.results },
                          ],
                      },
                  },
              }
            : null;

    // Transform Followers Data
    let followersData = null;
    if (
        engagement?.followers &&
        Array.isArray(statistics) &&
        statistics.length > 0
    ) {
        const validValues = statistics
            .filter((stat) => stat?.date && stat?.followers != null)
            .map((stat) => ({
                end_time: stat.date,
                value: stat.followers,
            }));

        if (validValues.length > 0) {
            followersData = {
                total_followers: engagement.followers,
                raw_data: {
                    description:
                        "Total followers and online follower statistics over time.",
                    values: validValues,
                },
            };
        }
    }

    // Transform Gender Demographics Data
    const genderAggregated = {};
    if (Array.isArray(demographics)) {
        demographics.forEach((demo) => {
            if (demo?.gender && demo?.value != null) {
                genderAggregated[demo.gender] =
                    (genderAggregated[demo.gender] || 0) + demo.value;
            }
        });
    }

    const genderData =
        Object.keys(genderAggregated).length > 0
            ? {
                  raw_data: {
                      description: "Follower distribution by gender.",
                      total_value: {
                          breakdowns: [
                              {
                                  results: Object.entries(genderAggregated).map(
                                      ([gender, value]) => ({
                                          dimension_values: [gender],
                                          value,
                                      })
                                  ),
                              },
                          ],
                      },
                  },
              }
            : null;

    // Transform Age Demographics Data
    const ageAggregated = {};
    if (Array.isArray(demographics)) {
        demographics.forEach((demo) => {
            if (demo?.age_range && demo?.value != null) {
                ageAggregated[demo.age_range] =
                    (ageAggregated[demo.age_range] || 0) + demo.value;
            }
        });
    }

    const ageData =
        Object.keys(ageAggregated).length > 0
            ? {
                  raw_data: {
                      description: "Follower distribution by age range.",
                      total_value: {
                          breakdowns: [
                              {
                                  results: Object.entries(ageAggregated).map(
                                      ([age_range, value]) => ({
                                          dimension_values: [age_range],
                                          value,
                                      })
                                  ),
                              },
                          ],
                      },
                  },
              }
            : null;

    // Transform City Demographics Data - simplified, pass data directly
    const cityFollowersData =
        geo_config?.followers &&
        Array.isArray(geo_config.followers) &&
        geo_config.followers.length > 0
            ? geo_config.followers.filter(
                  (city) => city?.name && city?.value != null
              )
            : null;

    const cityAudienceData =
        geo_config?.audience &&
        Array.isArray(geo_config.audience) &&
        geo_config.audience.length > 0
            ? geo_config.audience.filter(
                  (city) => city?.name && city?.value != null
              )
            : null;

    return {
        viewsData,
        interactionsData,
        followersData,
        genderData,
        ageData,
        cityFollowersData,
        cityAudienceData,
        accountName: profile?.username || profile?.title || "Unknown Account",
    };
};

const ChannelInsightsDisplay = ({ channelData, platform, account }) => {
    const transformedData = useMemo(() => {
        if (!channelData) return null;

        try {
            return transformChannelData(channelData);
        } catch (error) {
            console.error("Error transforming channel data:", error);
            return null;
        }
    }, [channelData]);

    const platformAccountUrl = useMemo(() => {
        if (!platform || !account?.platforms?.[platform]) return "#";

        const baseUrls = {
            instagram: `https://www.instagram.com/${account?.platforms?.instagram?.username || ""}`,
            youtube: `https://www.youtube.com/channel/${account?.platforms?.youtube?.id || ""}`,
            // Add other platforms here if needed
        };

        return baseUrls[platform.toLowerCase()] || "#";
    }, [platform, account]);

    const commonSuggestion =
        "Try selecting a wider date range for more comprehensive analytics.";

    if (!transformedData) {
        return (
            <Box display="flex" justifyContent="center" my={4}>
                <Typography>
                    No insights data available for the selected period.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Typography
                variant="h4"
                margin={"2rem 0"}
                textTransform={"capitalize"}
                textAlign="center"
            >
                {account?.name || "Account"}
                {" - "}
                <Link
                    style={{
                        color: "#008080",
                        fontWeight: "normal",
                    }}
                    to={platformAccountUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {account?.platforms?.[platform]?.username || "Username"}
                </Link>
            </Typography>

            {transformedData.viewsData ? (
                <Views data={transformedData.viewsData} />
            ) : (
                <DataUnavailableMessage
                    icon={VisibilityOffIcon}
                    title="Views"
                    message="View data is unavailable for the selected date range. Please increase the date range to see follower and content type view statistics."
                    suggestion={commonSuggestion}
                />
            )}

            {transformedData.interactionsData ? (
                <Interactions data={transformedData.interactionsData} />
            ) : (
                <DataUnavailableMessage
                    icon={WarningAmberIcon}
                    title="Interactions"
                    message="Interaction data is unavailable for the selected date range. Please increase the date range to see engagement statistics by content type."
                    suggestion={commonSuggestion}
                />
            )}

            {transformedData.followersData ? (
                <Followers data={transformedData.followersData} />
            ) : (
                <DataUnavailableMessage
                    icon={WarningAmberIcon}
                    title="Followers"
                    message="Follower data is unavailable for the selected date range. Please increase the date range to see total followers and online follower statistics."
                    suggestion={commonSuggestion}
                />
            )}

            {transformedData.genderData ? (
                <GenderDemographics data={transformedData.genderData} />
            ) : (
                <DataUnavailableMessage
                    icon={WarningAmberIcon}
                    title="Gender Demographics"
                    message="Gender distribution data is unavailable for the selected date range. Please increase the date range to see the gender distribution chart."
                    suggestion={commonSuggestion}
                />
            )}

            {transformedData.ageData ? (
                <AgeDemographics data={transformedData.ageData} />
            ) : (
                <DataUnavailableMessage
                    icon={WarningAmberIcon}
                    title="Age Demographics"
                    message="Age distribution data is unavailable for the selected date range. Please increase the date range to see the age distribution chart."
                    suggestion={commonSuggestion}
                />
            )}

            {transformedData.cityFollowersData ? (
                <CityDemographics
                    data={transformedData.cityFollowersData}
                    title="Followers City Demographics"
                />
            ) : (
                <DataUnavailableMessage
                    icon={WarningAmberIcon}
                    title="Followers City Demographics"
                    message="Followers city distribution data is unavailable for the selected date range. Please increase the date range to see the geographical distribution of your followers."
                    suggestion={commonSuggestion}
                />
            )}

            {transformedData.cityAudienceData ? (
                <CityDemographics
                    data={transformedData.cityAudienceData}
                    title="Audience City Demographics"
                />
            ) : (
                <DataUnavailableMessage
                    icon={WarningAmberIcon}
                    title="Audience City Demographics"
                    message="Audience city distribution data is unavailable for the selected date range. Please increase the date range to see the geographical distribution of your audience."
                    suggestion={commonSuggestion}
                />
            )}
        </>
    );
};

export default ChannelInsightsDisplay;
